/**
 * Fonctions d'accès aux routes d'authentification.
 * Les signatures s'appuient sur les contrats de `@noryvox/shared` (document 06 § 3).
 */

import {
  AUTH_ROUTES,
  MFA_ROUTES,
  type AuthProfile,
  type AuthSessionResponse,
  type InvitationPreview,
  type InvitationPreviewInput,
  type LoginInput,
  type MfaBackupCodesInput,
  type MfaBackupCodesResponse,
  type MfaDisableInput,
  type MfaSetupResponse,
  type MfaVerifyInput,
  type RegisterInvitationInput,
  type RegisterCompanyInput,
} from '@noryvox/shared';

import { apiRequest } from './client';

/** Charge utile de déconnexion (le jeton est optionnel : la purge locale reste possible). */
export interface LogoutApiPayload {
  refreshToken?: string;
  allDevices?: boolean;
}

/** `POST /auth/login` — ouvre une session et retourne les jetons et le contexte société. */
export async function loginApi(payload: LoginInput): Promise<AuthSessionResponse> {
  return apiRequest<AuthSessionResponse>({ method: 'POST', url: AUTH_ROUTES.login, data: payload });
}

/** `POST /auth/register-company` — crée la société et son administrateur, puis ouvre la session. */
export async function registerCompanyApi(
  payload: RegisterCompanyInput,
): Promise<AuthSessionResponse> {
  return apiRequest<AuthSessionResponse>({
    method: 'POST',
    url: AUTH_ROUTES.registerCompany,
    data: payload,
  });
}

/** `POST /auth/invitation-preview` — valide le lien public et retourne son contexte lisible. */
export async function previewInvitationApi(
  payload: InvitationPreviewInput,
): Promise<InvitationPreview> {
  return apiRequest<InvitationPreview>({
    method: 'POST',
    url: AUTH_ROUTES.invitationPreview,
    data: payload,
  });
}

/** `POST /auth/register-invitation` — crée le compte invité et ouvre la session. */
export async function registerInvitationApi(
  payload: RegisterInvitationInput,
): Promise<AuthSessionResponse> {
  return apiRequest<AuthSessionResponse>({
    method: 'POST',
    url: AUTH_ROUTES.registerInvitation,
    data: payload,
  });
}

/** `POST /auth/refresh` — échange un jeton de rafraîchissement contre un nouveau couple de jetons. */
export async function refreshApi(refreshToken: string): Promise<AuthSessionResponse> {
  return apiRequest<AuthSessionResponse>({
    method: 'POST',
    url: AUTH_ROUTES.refresh,
    data: { refreshToken },
  });
}

/** `POST /auth/logout` — révoque la session courante (ou toutes) ; réponse sans contenu. */
export async function logoutApi(payload: LogoutApiPayload = {}): Promise<void> {
  await apiRequest<void>({ method: 'POST', url: AUTH_ROUTES.logout, data: payload });
}

/** `GET /auth/me` — profil complet, contexte société actif et sociétés disponibles. */
export async function getMeApi(): Promise<AuthProfile> {
  return apiRequest<AuthProfile>({ method: 'GET', url: AUTH_ROUTES.me });
}

/** `POST /auth/switch-tenant` — change la société active et retourne une nouvelle session. */
export async function switchTenantApi(tenantId: string): Promise<AuthSessionResponse> {
  return apiRequest<AuthSessionResponse>({
    method: 'POST',
    url: AUTH_ROUTES.switchTenant,
    data: { tenantId },
  });
}

/** `POST /auth/mfa/setup` — démarre l'enrôlement TOTP (secret et QR code à afficher une fois). */
export async function setupMfaApi(): Promise<MfaSetupResponse> {
  return apiRequest<MfaSetupResponse>({ method: 'POST', url: MFA_ROUTES.setup });
}

/** `POST /auth/mfa/verify` — confirme l'enrôlement et retourne les codes de secours. */
export async function verifyMfaApi(payload: MfaVerifyInput): Promise<MfaBackupCodesResponse> {
  return apiRequest<MfaBackupCodesResponse>({
    method: 'POST',
    url: MFA_ROUTES.verify,
    data: payload,
  });
}

/** `POST /auth/mfa/disable` — désactive le second facteur (mot de passe + code requis). */
export async function disableMfaApi(payload: MfaDisableInput): Promise<void> {
  await apiRequest<void>({ method: 'POST', url: MFA_ROUTES.disable, data: payload });
}

/** `POST /auth/mfa/backup-codes` — régénère les codes de secours (mot de passe requis). */
export async function regenerateBackupCodesApi(
  payload: MfaBackupCodesInput,
): Promise<MfaBackupCodesResponse> {
  return apiRequest<MfaBackupCodesResponse>({
    method: 'POST',
    url: MFA_ROUTES.backupCodes,
    data: payload,
  });
}
